import React from 'react'
import { toast } from 'react-toastify'
import { v4 as uuid } from 'uuid'
import { makeAutoObservable } from 'mobx'

import CustomToast from './CustomToast'

class NotificationHandler {
    persistedToasts = new Map()

    constructor(store) {
        this.store = store
        makeAutoObservable(this, { store: false })
    }

    createPromiseToast = ({
        promise, pending, success, error, title, persist,
    }) => {
        const id = uuid()
        const errorBody = (e) => (typeof error === 'function' ? error(e) : error)

        const tracked = toast.promise(promise, {
            pending: { render: () => <CustomToast toastProps={{ title, body: pending }} /> },
            success: { render: () => <CustomToast toastProps={{ title, body: success }} /> },
            error: {
                render: ({ data }) => <CustomToast toastProps={{ title, body: errorBody(data) }} />,
                autoClose: persist ? false : undefined,
                onClose: persist ? () => this.store?.errorHandler?.clearPersistedError(id) : undefined,
            },
        }, { toastId: id })

        // the caller keeps its own reference to `promise` if it needs the outcome;
        // this chain is swallowed so an untracked failure cannot surface as an unhandled rejection
        return tracked.catch((e) => {
            if (persist) this.store?.errorHandler?.registerPersistedError(id, { title, body: errorBody(e) })
            return undefined
        })
    }

    createToast = (data, persist = false) => {
        if (persist) {
            this.persistToast(data)
            return
        }

        switch (data.level) {
            case 'success':
                toast.success(CustomToast, {
                    ...data,
                    autoClose: 2000,
                })
                break
            case 'warning':
                toast.warning(CustomToast, {
                    ...data,
                })
                break
            default:
                toast(CustomToast, {
                    ...data,
                })
        }
    }

    persistToast = (data) => {
        const id = uuid()
        this.persistedToasts.set(id, {
            ...data,
            onClose: () => this.clearPersistedError(id),
        })

        toast.error(CustomToast, {
            autoClose: false,
            ...data,
            onClose: () => this.clearPersistedError(id),
        })
    }

    clearPersistedError = (id) => {
        if (!this.persistedToasts.has(id)) return

        this.persistedToasts.delete(id)
    }
}

export default NotificationHandler
